import { useState, useEffect, useRef } from "react";
// ─────────────────────────────────────────────────────────────────────────
// Corrigé le 23/09/2026 (bug "Access denied" sur le 2FA — voir
// audit-securite-2026-09-22.md) : ce composant vit dans le frontend ADMIN
// et n'a donc AUCUNE raison d'appeler les routes personnelles
// (/credentials/{id}/totp), qui passent par checkCompanyAccess côté
// backend et comparent l'entreprise de la credential à celle de l'admin
// connecté (CPUC) — d'où l'échec pour toute credential appartenant à une
// autre entreprise que la sienne. Les routes admin existent déjà côté
// backend (CredentialController.adminGetTotpCode / adminSaveTotpSecret /
// adminDeleteTotpSecret, /credentials/admin/{id}/totp) et bypassent cette
// vérification — il suffisait de les appeler depuis ce widget, ce qui
// n'avait jamais été fait.
import { adminGetTotpCode, adminSaveTotpSecret, adminDeleteTotpSecret, adminLogCredentialAccess } from "../../services/credentialService.js";

export default function TotpWidget({ credentialId, hasTotpSecret, onUpdate, readOnly = false }) {

  const [code,      setCode]      = useState(null);
  const [remaining, setRemaining] = useState(30);
  const [loading,   setLoading]   = useState(false);
  const [copied,    setCopied]    = useState(false);
  const [showSetup, setShowSetup] = useState(false);
  const [secret,    setSecret]    = useState("");
  const [saving,    setSaving]    = useState(false);
  const [error,     setError]     = useState("");
  const intervalRef = useRef(null);
  // Le code est recalculé chaque seconde pour rafraîchir le compte à rebours —
  // on ne journalise qu'une seule "consultation" par ouverture du widget,
  // pas à chaque rafraîchissement, sinon le journal d'accès serait noyé
  // sous une entrée par seconde.
  const hasLoggedViewRef = useRef(false);

  useEffect(() => {
    if (!hasTotpSecret) return;
    hasLoggedViewRef.current = false;
    const refresh = async () => {
      try {
        const data = await adminGetTotpCode(credentialId);
        setCode(data.code);
        setRemaining(data.secondsRemaining);
        if (!hasLoggedViewRef.current) {
          hasLoggedViewRef.current = true;
          adminLogCredentialAccess(credentialId, { targetType: "TOTP", action: "VIEW" }).catch(() => {});
        }
      } catch {}
    };
    setLoading(true);
    refresh().finally(() => setLoading(false));
    intervalRef.current = setInterval(refresh, 1000);
    return () => clearInterval(intervalRef.current);
  }, [credentialId, hasTotpSecret]);

  const handleCopy = () => {
    if (!code) return;
    adminLogCredentialAccess(credentialId, { targetType: "TOTP", action: "COPY" }).catch(() => {});
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSaveSecret = async () => {
    setError("");
    if (!secret.trim()) return setError("Le secret est obligatoire.");
    setSaving(true);
    try {
      await adminSaveTotpSecret(credentialId, secret.trim());
      setSecret("");
      setShowSetup(false);
      onUpdate?.(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Désactiver le 2FA pour cette credential ?")) return;
    try {
      await adminDeleteTotpSecret(credentialId);
      setCode(null);
      clearInterval(intervalRef.current);
      onUpdate?.(false);
    } catch (err) {
      alert(err.message);
    }
  };

  const fmtCode = (c) => c ? `${c.slice(0, 3)} ${c.slice(3)}` : "— —";
  const pct = (remaining / 30) * 100;
  const barColor = remaining > 10 ? "#E8412F" : remaining > 5 ? "#F59E0B" : "#8B1A12";

  // ── Secret configuré → affiche le code ──────────────────────────────────
  if (hasTotpSecret) {
    return (
      <div style={{ background:"#F4F5F7", border:"1.5px solid #D1D5DB", borderRadius:10, padding:"12px 14px" }}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10 }}>
          <div style={{ display:"flex", alignItems:"center", gap:6, fontSize:12, fontWeight:600, color:"#374151" }}>
            <i className="bi bi-shield-lock-fill" style={{ color:"#E8412F" }}/>
            Code 2FA
          </div>
          {!readOnly && (
            <button onClick={handleDelete}
              style={{ fontSize:11, background:"none", border:"none", cursor:"pointer", color:"#DC2626" }}
              title="Désactiver le 2FA">
              <i className="bi bi-x-circle me-1"/>Désactiver
            </button>
          )}
        </div>

        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <div style={{ fontFamily:"monospace", fontSize:28, fontWeight:700, color:"#111827", letterSpacing:".15em", flex:1 }}>
            {loading ? "— —" : fmtCode(code)}
          </div>
          <button onClick={handleCopy} disabled={!code}
            style={{ background:"rgba(232,65,47,.1)", border:"1px solid rgba(232,65,47,.3)", borderRadius:8, padding:"6px 12px", color:"#E8412F", fontSize:12, fontWeight:600, cursor:"pointer", display:"flex", alignItems:"center", gap:6 }}>
            <i className={`bi ${copied ? "bi-check2" : "bi-copy"}`}/>
            {copied ? "Copié !" : "Copier"}
          </button>
        </div>

        <div style={{ marginTop:8 }}>
          <div style={{ height:4, background:"#E5E7EB", borderRadius:99, overflow:"hidden" }}>
            <div style={{ height:"100%", borderRadius:99, width:`${pct}%`, background:barColor, transition:"width 1s linear, background .5s" }}/>
          </div>
          <div style={{ display:"flex", justifyContent:"flex-end", marginTop:4, fontSize:11, color:"#9CA3AF" }}>
            Renouvellement dans {remaining}s
          </div>
        </div>
      </div>
    );
  }

  // ── Pas de secret → formulaire d'activation ─────────────────────────────
  if (readOnly) return null;

  return (
    <div style={{ background:"#F4F5F7", border:"1.5px dashed #D1D5DB", borderRadius:10, padding:"12px 14px" }}>
      {!showSetup ? (
        <button onClick={() => setShowSetup(true)}
          style={{ width:"100%", background:"none", border:"none", cursor:"pointer", display:"flex", alignItems:"center", gap:8, padding:0 }}>
          <i className="bi bi-shield-plus" style={{ fontSize:16, color:"#E8412F" }}/>
          <div style={{ textAlign:"left" }}>
            <div style={{ fontWeight:600, color:"#374151", fontSize:13 }}>Activer le 2FA</div>
            <div style={{ fontSize:11, marginTop:2, color:"#6B7280" }}>Générer les codes TOTP pour cette application</div>
          </div>
        </button>
      ) : (
        <div>
          <div style={{ fontSize:12, fontWeight:700, color:"#374151", marginBottom:10, display:"flex", alignItems:"center", gap:6 }}>
            <i className="bi bi-shield-lock-fill" style={{ color:"#E8412F" }}/>
            Configurer le 2FA
          </div>

          <div style={{ fontSize:12, color:"#4B5563", marginBottom:10, lineHeight:1.7 }}>
            <strong style={{ color:"#111827" }}>Comment obtenir le secret ?</strong>
            <ol style={{ margin:"6px 0 0 16px", padding:0 }}>
              <li>Activez le 2FA dans l'application tierce (GitHub, Google…)</li>
              <li>Choisissez "Configurer une app d'authentification"</li>
              <li>Cherchez le lien "Entrer le code manuellement" pour voir le secret</li>
              <li>Copiez-le ici</li>
            </ol>
          </div>

          {error && (
            <div style={{ fontSize:12, color:"#DC2626", background:"rgba(220,38,38,.08)", border:"1px solid rgba(220,38,38,.2)", borderRadius:7, padding:"7px 10px", marginBottom:8 }}>
              {error}
            </div>
          )}

          <div style={{ display:"flex", gap:6 }}>
            <input
              style={{
                flex:1,
                background:"#fff",
                border:"1.5px solid #D1D5DB",
                borderRadius:7,
                color:"#111827",
                padding:"8px 10px",
                fontSize:13,
                fontFamily:"monospace",
                outline:"none",
                letterSpacing:".08em",
              }}
              value={secret}
              onChange={e => setSecret(e.target.value.replace(/\s/g, "").toUpperCase())}
              placeholder="JBSWY3DPEHPK3PXP"
            />
            <button onClick={handleSaveSecret} disabled={saving || !secret.trim()}
              style={{ background:"rgba(232,65,47,.1)", border:"1.5px solid rgba(232,65,47,.35)", borderRadius:7, color:"#E8412F", fontSize:12, fontWeight:700, padding:"8px 14px", cursor:"pointer", whiteSpace:"nowrap" }}>
              {saving ? <i className="bi bi-arrow-repeat spin"/> : "Activer"}
            </button>
            <button onClick={() => { setShowSetup(false); setSecret(""); setError(""); }}
              style={{ background:"#fff", border:"1.5px solid #D1D5DB", borderRadius:7, color:"#6B7280", fontSize:12, padding:"8px 12px", cursor:"pointer" }}>
              Annuler
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        .spin { animation: spin .7s linear infinite; display: inline-block; }
      `}</style>
    </div>
  );
}